import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
    createJournal, getJournal, updateJournal, deleteJournal,
    createNote, getNote, updateNote, deleteNote,
    fetchCategories, dismissName,
} from "@/lib/api";
import MentionEditor from "@/components/MentionEditor";
import CategoryChips from "@/components/CategoryChips";
import { ArrowLeft, Trash2, Save } from "lucide-react";

/**
 * Unified composer used for both journal and notes.
 * Route params: space = "journal" | "notes"; id = "new" or existing id.
 */
export default function EntryComposer({ space }) {
    const { id } = useParams();
    const nav = useNavigate();
    const [sp] = useSearchParams();
    const isNew = id === "new" || !id;

    const [content, setContent] = useState([]);
    const [title, setTitle] = useState("");
    const [tagsStr, setTagsStr] = useState("");
    const [categoryId, setCategoryId] = useState(null);
    const [cats, setCats] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [keptMentions, setKeptMentions] = useState([]);
    const [savedId, setSavedId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [initialContent, setInitialContent] = useState(null);

    // Load categories
    useEffect(() => { fetchCategories(space).then(d => setCats(d.categories || [])); }, [space]);

    // Suggest category via query (e.g. from "Write the story")
    useEffect(() => {
        const suggest = sp.get("suggestCategory");
        if (suggest && cats.length && !categoryId) {
            const c = cats.find(x => x.name.toLowerCase() === suggest.toLowerCase());
            if (c) setCategoryId(c.id);
        }
    }, [sp, cats, categoryId]);

    // Load existing entry
    const initOnce = React.useRef(false);
    useEffect(() => {
        if (initOnce.current) return;
        initOnce.current = true;
        if (isNew) {
            const initial = [];
            const fromPassage = sp.get("fromPassage") === "1";
            const fromQuestion = sp.get("fromQuestion") === "1";
            if (fromPassage || fromQuestion) {
                const key = fromQuestion ? "pending-question-card" : "pending-passage-card";
                try {
                    const raw = sessionStorage.getItem(key);
                    if (raw) {
                        const p = JSON.parse(raw);
                        const uid = `n_${Math.random().toString(36).slice(2, 10)}`;
                        initial.push({
                            type: "verse_card",
                            node_id: uid,
                            reference: p.refs?.[0] || p.reference || "",
                            refs: p.refs || (p.reference ? [p.reference] : []),
                            label: p.label || "",
                            translation: p.translation || "kjv",
                            text: p.text || "",
                            format: p.format || "full",
                        });
                        initial.push({ type: "text", text: fromQuestion ? "\n\n" : " " });
                        sessionStorage.removeItem(key);
                    }
                } catch (_e) { /* ignore */ }
            }
            const mentionSubject = sp.get("mentionSubject");
            const mentionName = sp.get("mentionName");
            if (mentionSubject && mentionName) {
                initial.push({
                    type: "mention",
                    node_id: `n_${Math.random().toString(36).slice(2,10)}`,
                    subject_id: mentionSubject,
                    name: mentionName,
                });
                initial.push({ type: "text", text: " " });
            }

            // Goal-driven prompts (completion or weekly reflection) — a soft prefill body.
            const fromGoal = sp.get("fromGoal") === "1";
            const fromGoalReflect = sp.get("fromGoalReflect") === "1";
            if (fromGoal || fromGoalReflect) {
                const key = fromGoalReflect ? "goal-weekly-reflect-prompt" : "goal-completion-prompt";
                try {
                    const raw = sessionStorage.getItem(key);
                    if (raw) {
                        const p = JSON.parse(raw);
                        if (p.verse && p.verse.reference) {
                            initial.push({
                                type: "verse_card",
                                node_id: `n_${Math.random().toString(36).slice(2, 10)}`,
                                reference: p.verse.reference,
                                refs: [p.verse.reference],
                                label: p.verse.reference,
                                translation: (p.verse.translation || "kjv"),
                                text: p.verse.text || "",
                                format: "full",
                            });
                        }
                        if (p.body) {
                            initial.push({ type: "text", text: p.body });
                        }
                        sessionStorage.removeItem(key);
                    }
                } catch (_e) { /* ignore */ }
            }
            setInitialContent(initial);
            return;
        }
        (async () => {
            if (space === "journal") {
                const d = await getJournal(id);
                setContent(d.entry.content || []);
                setInitialContent(d.entry.content || []);
                setCategoryId(d.entry.category_id || null);
            } else {
                const d = await getNote(id);
                setContent(d.note.content || []);
                setInitialContent(d.note.content || []);
                setTitle(d.note.title || "");
                setTagsStr((d.note.tags || []).join(", "));
                setCategoryId(d.note.category_id || null);
            }
        })();
    }, [id, isNew, space, sp]);

    const save = async () => {
        setSaving(true);
        try {
            const isFresh = !savedId && isNew;
            if (space === "journal") {
                if (isFresh) {
                    const r = await createJournal({ content, category_id: categoryId });
                    setSavedId(r.entry.id);
                    setSuggestions(r.suggestions || []);
                    setKeptMentions(r.kept_mentions || []);
                    window.history.replaceState(null, "", `/journal/${r.entry.id}/edit`);
                } else {
                    const targetId = savedId || id;
                    await updateJournal(targetId, { content, category_id: categoryId });
                    setSavedId(targetId);
                }
            } else {
                const tags = tagsStr.split(",").map(t => t.trim()).filter(Boolean);
                if (isFresh) {
                    const r = await createNote({ title, tags, content, category_id: categoryId });
                    setSavedId(r.note.id);
                    window.history.replaceState(null, "", `/notes/${r.note.id}/edit`);
                } else {
                    const targetId = savedId || id;
                    await updateNote(targetId, { title, tags, content, category_id: categoryId });
                    setSavedId(targetId);
                }
            }
        } finally { setSaving(false); }
    };

    const del = async () => {
        if (!id || isNew) return;
        if (!window.confirm(space === "journal" ? "Delete this entry?" : "Delete this note?")) return;
        if (space === "journal") await deleteJournal(id);
        else await deleteNote(id);
        nav(space === "journal" ? "/journal" : "/notes");
    };

    const doneSuggestion = async (name, add) => {
        if (add) {
            // Insert a mention chip at end of current content
            const { createSubject } = await import("@/lib/api");
            const s = await createSubject(name);
            const newContent = [...content,
                { type: "text", text: " " },
                { type: "mention", node_id: `n_${Math.random().toString(36).slice(2,10)}`, subject_id: s.id, name: s.name },
            ];
            setContent(newContent);
            if (savedId) await updateJournal(savedId, { content: newContent });
        } else {
            await dismissName(name);
        }
        setSuggestions(suggestions.filter(n => n !== name));
    };

    return (
        <div data-testid="entry-composer" className="px-5 md:px-8 py-4 md:py-6 pb-40 mx-auto w-full" style={{ maxWidth: "68ch" }}>
            <div className="flex items-center justify-between mb-3">
                <button
                    onClick={() => nav(space === "journal" ? "/journal" : "/notes")}
                    className="inline-flex items-center gap-1.5 font-ui text-[13px] text-ink-70 hover:text-ink"
                >
                    <ArrowLeft size={14} /> {space === "journal" ? "Journal" : "Notes"}
                </button>
                <div className="flex gap-2">
                    {!isNew && (
                        <button
                            data-testid="delete-entry"
                            onClick={del}
                            className="w-9 h-9 rounded-full flex items-center justify-center text-ink-70 hover:text-plum hover:bg-plum/10 transition"
                            aria-label="Delete"
                        >
                            <Trash2 size={16} />
                        </button>
                    )}
                    <button
                        data-testid="save-entry"
                        onClick={save}
                        disabled={saving}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-moss text-white font-ui text-[13px] font-medium hover:bg-moss-deep transition disabled:opacity-50"
                    >
                        <Save size={14} /> {saving ? "Saving…" : (savedId ? "Saved" : "Save")}
                    </button>
                </div>
            </div>

            {space === "notes" && (
                <>
                    <input
                        data-testid="note-title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Title"
                        className="w-full font-scripture scripture text-3xl md:text-4xl tracking-tight text-ink outline-none bg-transparent placeholder:text-ink-40 mb-3"
                    />
                    <input
                        data-testid="note-tags"
                        value={tagsStr}
                        onChange={(e) => setTagsStr(e.target.value)}
                        placeholder="tags, comma-separated (e.g. sermon, study)"
                        className="w-full font-ui text-[13px] text-ink-70 outline-none bg-transparent placeholder:text-ink-40 mb-4"
                    />
                </>
            )}

            <div className="mb-3">
                <CategoryChips
                    space={space}
                    categories={cats}
                    selectedId={categoryId}
                    onSelect={setCategoryId}
                    variant="picker"
                    onCategoriesChanged={() => fetchCategories(space).then(d => setCats(d.categories || []))}
                />
            </div>

            {initialContent !== null && (
                <MentionEditor
                    initialValue={initialContent}
                    onChange={setContent}
                    space={space}
                    version={(localStorage.getItem("matthew-reader-version") || "kjv").toLowerCase()}
                    autoFocus
                />
            )}

            {space === "journal" && suggestions.length > 0 && (
                <div data-testid="suggestions-block" className="mt-4 space-y-2">
                    {suggestions.map(name => (
                        <div
                            key={name}
                            data-testid={`suggest-${name}`}
                            className="flex items-center justify-between p-3 rounded-2xl bg-plum/5 border border-plum/20"
                        >
                            <div className="font-ui text-[13px] text-ink">
                                Add “<span className="font-semibold text-plum">{name}</span>” to your praying-for list?
                            </div>
                            <div className="flex gap-2">
                                <button data-testid={`suggest-add-${name}`} onClick={() => doneSuggestion(name, true)} className="px-3 py-1.5 rounded-full bg-plum text-white font-ui text-[12px]">Add</button>
                                <button data-testid={`suggest-dismiss-${name}`} onClick={() => doneSuggestion(name, false)} className="px-3 py-1.5 rounded-full font-ui text-[12px] text-ink-70">Dismiss</button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {space === "journal" && keptMentions.length > 0 && (
                <div data-testid="kept-mentions-block" className="mt-4 space-y-2">
                    {keptMentions.map(k => (
                        <div
                            key={k.subject_id}
                            data-testid={`kept-return-${k.subject_id}`}
                            className="flex items-center justify-between p-3 rounded-2xl bg-plum/5 border border-plum/20"
                        >
                            <div className="font-ui text-[13px] text-ink">
                                Return <span className="font-semibold text-plum">{k.name}</span> to your active list?
                            </div>
                            <div className="flex gap-2">
                                <button
                                    data-testid={`kept-return-accept-${k.subject_id}`}
                                    onClick={async () => {
                                        const { returnToActive } = await import("@/lib/api");
                                        await returnToActive(k.subject_id);
                                        setKeptMentions(keptMentions.filter(x => x.subject_id !== k.subject_id));
                                    }}
                                    className="px-3 py-1.5 rounded-full bg-plum text-white font-ui text-[12px]"
                                >
                                    Return
                                </button>
                                <button
                                    data-testid={`kept-return-dismiss-${k.subject_id}`}
                                    onClick={() => setKeptMentions(keptMentions.filter(x => x.subject_id !== k.subject_id))}
                                    className="px-3 py-1.5 rounded-full font-ui text-[12px] text-ink-70"
                                >
                                    Not now
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
